import { memo } from 'react';
import {
	CapacityChart,
	ClientDeparturesChart,
	DashboardCard,
	DeadlineStatusDonut,
	DeadlinesByMonthChart,
	StageBars,
	UpcomingDeparturesList,
} from './index';
import { OverviewFilters } from './OverviewFilters';
import type { FilterField } from './OverviewFilters';
import type { Status } from '../../../utils/panorama/panoramaPeopleRows';
import type { TodaySnapshot } from '../../../utils/panorama/panoramaToday';
import type { CapacitySnapshot } from '../../../utils/panorama/panoramaCapacity';

interface DashboardScreenProps {
	today: TodaySnapshot;
	capacity: CapacitySnapshot;
	statusCounts: Record<Status | 'all', number>;
	clientDepartures: any;
	deadlinesByMonth: any[];
	referenceDate: Date;
	capacityLimitPercent: number;
	filterFields: FilterField[];
	onClearFilters: () => void;
	onStatusClick?: (status: Status) => void;
	onWeekClick?: (weekStart: string, statuses: Status | Status[]) => void;
	onMonthClick?: (year: number, monthNum: number, isPast: boolean) => void;
	onClientClick?: (clientName: string, statuses: Status[]) => void;
	onStageClick?: (stage: 'manager' | 'client' | 'dp' | 'correction') => void;
	pendingCounts?: Record<'manager' | 'client' | 'dp' | 'correction', number>;
	pendingTotal?: number;
	deadlineMonthsAhead: number;
	onDeadlineMonthsAheadChange: (months: number) => void;
	periodSubtitle?: string;
}

const SELECT_CLASS =
	'rounded-lg border border-raro-gray-200 bg-white pl-3 pr-8 py-1 text-xs text-raro-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-300 min-w-[7.5rem]';

function DashboardScreenComponent({
	today,
	capacity,
	statusCounts,
	clientDepartures,
	deadlinesByMonth,
	referenceDate,
	capacityLimitPercent,
	filterFields,
	onClearFilters,
	onStatusClick,
	onWeekClick,
	onMonthClick,
	onClientClick,
	onStageClick,
	pendingCounts = { manager: 0, client: 0, dp: 0, correction: 0 },
	pendingTotal = 0,
	deadlineMonthsAhead,
	onDeadlineMonthsAheadChange,
	periodSubtitle,
}: DashboardScreenProps) {

	return (
		<div className="space-y-4 p-3 md:p-6">
			<OverviewFilters fields={filterFields} onClear={onClearFilters} subtitle={periodSubtitle} />

			<div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
				<DashboardCard
					tourId="overview-capacity"
					className="lg:col-span-2"
					title="Pessoas em férias por semana"
					subtitle={`${capacity.weeks.length} semanas · ${capacity.weeksAboveLimit} acima do limite de ${capacityLimitPercent}%`}
				>
					<CapacityChart snapshot={capacity} onWeekClick={onWeekClick} />
				</DashboardCard>

				<DashboardCard tourId="overview-deadline-status" title="Situação dos prazos" subtitle="Art. 137 da CLT: férias não gozadas no prazo geram pagamento em dobro">
					<DeadlineStatusDonut statusCounts={statusCounts} onStatusClick={onStatusClick} />
				</DashboardCard>

				<DashboardCard
					tourId="overview-clients"
					className="lg:col-span-2"
					title="Saídas por cliente"
					subtitle="Quem está, estará ou pediu férias no período selecionado · cliente atual de cada pessoa"
				>
					<ClientDeparturesChart
						clients={clientDepartures.clients}
						onClientClick={onClientClick}
					/>
				</DashboardCard>

				<DashboardCard
					tourId="overview-upcoming"
					title="Próximas saídas"
					subtitle="Quem sai nos próximos 30 dias"
					action={
						today.upcomingDepartures.length > 0 && (
							<span className="shrink-0 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-semibold text-primary-700">
								{today.upcomingDepartures.length} pessoa{today.upcomingDepartures.length !== 1 ? 's' : ''}
							</span>
						)
					}
				>
					<UpcomingDeparturesList departures={today.upcomingDepartures} />
				</DashboardCard>

				<DashboardCard
					tourId="overview-deadlines"
					className="lg:col-span-2"
					title="Prazos de férias: vencidos e a vencer"
					subtitle={`Todos os vencidos em aberto + próximos ${deadlineMonthsAhead} meses · cada pessoa conta no prazo mais antigo`}
					action={
						<select
							aria-label="Alcance de meses"
							className={SELECT_CLASS}
							value={deadlineMonthsAhead}
							onChange={e => onDeadlineMonthsAheadChange(Number(e.target.value))}
						>
							<option value={6}>6 meses</option>
							<option value={12}>12 meses</option>
							<option value={24}>24 meses</option>
						</select>
					}
				>
					<DeadlinesByMonthChart data={deadlinesByMonth} referenceDate={referenceDate} onMonthClick={onMonthClick} />
				</DashboardCard>

				<DashboardCard
					tourId="overview-stages"
					title="Pedidos por etapa"
					subtitle="Onde cada pedido está parado. Um pedido com o gestor e o cliente ao mesmo tempo conta nas duas etapas."
				>
					<StageBars counts={pendingCounts} total={pendingTotal} onStageClick={onStageClick} />
				</DashboardCard>
			</div>
		</div>
	);
}

export const DashboardScreen = memo(DashboardScreenComponent);
